import { Link } from '@tanstack/react-router'
import { Typography } from '@trellis/roots/typography'
import { computeInvoicingReadiness } from '@trellis/shared'
import type {
  InvoicingReadinessDocumentType,
  InvoicingReadinessField,
} from '@trellis/shared'
import { AlertTriangle } from 'lucide-react'

import { useCurrentTenantRole } from '@/hooks/useCurrentTenantRole'
import { useOrganization } from '@/hooks/useOrganization'
import type { FileRouteTypes } from '@/routeTree.gen'

const FIELD_LABELS: Record<InvoicingReadinessField, string> = {
  name: 'Business name',
  address: 'Business address',
  email: 'Business email',
  taxIdType: 'Tax ID type',
  taxIdValue: 'Tax ID',
  taxIdCustomLabel: 'Tax ID label',
  gstRegistered: 'GST registration status',
  defaultTaxType: 'Default tax type',
  defaultTaxRate: 'Default tax rate',
  defaultPaymentOption: 'A default payment option',
}

const FIELD_SETTINGS_PATH: Record<InvoicingReadinessField, FileRouteTypes['to']> = {
  name: '/settings/organization',
  address: '/settings/organization',
  email: '/settings/organization',
  taxIdType: '/settings/organization/tax',
  taxIdValue: '/settings/organization/tax',
  taxIdCustomLabel: '/settings/organization/tax',
  gstRegistered: '/settings/organization/tax',
  defaultTaxType: '/settings/organization/tax',
  defaultTaxRate: '/settings/organization/tax',
  defaultPaymentOption: '/settings/organization/payment-options',
}

// Only ADMIN/OWNER can save organization business/tax/payment settings
// (AdminGuard-protected endpoints) — see the TD-133 spec's Permissions Summary.
const CAN_FIX_ORGANIZATION_ROLES = new Set(['ADMIN', 'OWNER'])

interface OrganizationReadinessBannerProps {
  documentType: InvoicingReadinessDocumentType
  status: string
}

/**
 * Warns on a DRAFT invoice/quote when the org is missing business/tax (and, for invoices,
 * payment) details the backend readiness gate requires before it can be sent. Only shown
 * while DRAFT — once sent, the Send action this warns about is no longer available.
 */
export function OrganizationReadinessBanner({
  documentType,
  status,
}: OrganizationReadinessBannerProps) {
  const { data: organizationResult, isLoading } = useOrganization()
  const role = useCurrentTenantRole()

  if (status !== 'DRAFT' || isLoading) return null

  const organization = organizationResult?.data ?? null
  const { isReady, missingFields } = computeInvoicingReadiness(organization, documentType)
  if (isReady) return null

  const canFix = role !== null && CAN_FIX_ORGANIZATION_ROLES.has(role)
  const documentLabel = documentType === 'invoice' ? 'invoice' : 'quote'

  return (
    <div className="mb-4 flex items-start gap-3.5 rounded-xl border border-amber-100 bg-amber-50 p-4">
      <AlertTriangle className="mt-0.5 size-[18px] shrink-0 text-amber-600" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-amber-900">
          Your business details aren't complete enough to send this {documentLabel}.
        </p>
        <ul className="mt-1.5 space-y-1">
          {missingFields.map((field) =>
            canFix ? (
              <li key={field}>
                <Link
                  to={FIELD_SETTINGS_PATH[field]}
                  className="text-xs text-amber-900/85 underline-offset-2 hover:text-amber-900 hover:underline"
                >
                  {FIELD_LABELS[field]}
                </Link>
              </li>
            ) : (
              <li key={field}>
                <Typography variant="caption" className="text-amber-900/85">
                  {FIELD_LABELS[field]}
                </Typography>
              </li>
            ),
          )}
        </ul>
        <Typography variant="body-sm" className="mt-1.5 text-amber-900/85">
          {canFix
            ? 'Complete these in Settings before sending.'
            : 'Ask an admin or owner to complete these in Settings.'}
        </Typography>
      </div>
    </div>
  )
}
